
import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { candidateFoldersAPI, candidatesAPI2, authAPI, settingsAPI } from "@/services/api";
import { Loader2, FileText, User, X, Download } from "lucide-react";
import { uploadFileToS3, validateFile } from "@/utils/s3Upload";

const EditCandidateModal = ({ isOpen, onClose, candidateId, onSuccess }) => {

  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [existingResume, setExistingResume] = useState(null);
  const [existingProfileImage, setExistingProfileImage] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const resumeInputRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const { toast } = useToast();

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      mobileNumber: "",
      address: "",
      folderId: "",
    },
  });

  // Fetch candidate data when modal opens
  useEffect(() => {
  if (isOpen) {
    if (candidateId) {
      fetchCandidate();
    }
    fetchFolders();
    fetchUserProfile();
  }
}, [isOpen, candidateId]); // Add isOpen to dependencies

  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setUserProfile(response.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const fetchFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await candidateFoldersAPI.getAll();
      setFolders(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      toast({
        title: "Error",
        description: "Failed to load folders",
        variant: "destructive",
      });
    } finally {
      setLoadingFolders(false);
    }
  };

  const fetchCandidate = async () => {
    setLoading(true);
    try {
      const response = await candidatesAPI2.getOne(candidateId);
      const candidate = response.data;

      // Handle folderId - it could be an object (populated) or string (ObjectId)
      let folderIdValue = "";
      if (candidate.folderId) {
        folderIdValue = typeof candidate.folderId === 'object' 
          ? candidate.folderId._id 
          : candidate.folderId;
      }



      form.reset({
        name: candidate.name || "",
        email: candidate.email || "",
        mobileNumber: candidate.mobileNumber || "",
        address: candidate.address || "",
        folderId: folderIdValue,
      });

      setExistingResume(candidate.resume || null);
      setExistingProfileImage(candidate.profileImage || null);
      setProfileImagePreview(candidate.profileImage || null);

    } catch (error) {
      console.error("Error fetching candidate:", error);
      toast({
        title: "Error",
        description: "Failed to load candidate data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateFile(file, 'resume');
        setResumeFile(file);
      } catch (error) {
        toast({
          title: "Invalid File",
          description: error.message,
          variant: "destructive",
        });
        e.target.value = null;
      }
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateFile(file, 'profileImage');
        setProfileImageFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast({
          title: "Invalid File",
          description: error.message,
          variant: "destructive",
        });
        e.target.value = null;
      }
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = null;
    }
  };

  const removeProfileImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(existingProfileImage);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = null;
    }
  };

  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Debug: Check authentication status
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again",
          variant: "destructive",
        });
        return;
      }
      

      // Get user profile for companyId
      if (!userProfile) {
        throw new Error('User profile not loaded');
      }

      const companyId = userProfile.companyId;

      let resumeUrl = existingResume; // Keep existing resume by default
      let profileImageUrl = existingProfileImage; // Keep existing profile image by default

      // Upload new files to S3 if provided
      if (resumeFile || profileImageFile) {
        setUploadingFiles(true);

        // Test cloud storage settings access first
        try {
          const settingsResponse = await settingsAPI.getCloudStorageSettings();
        } catch (settingsError) {
          console.error('❌ Frontend: Cannot access cloud storage settings:', settingsError);
          throw new Error(`Cloud storage not configured: ${settingsError.response?.status} - ${settingsError.response?.data?.message || settingsError.message}`);
        }

        // Upload resume if new file selected
        if (resumeFile) {
          try {
            resumeUrl = await uploadFileToS3(resumeFile, companyId, candidateId, 'resume');
          } catch (uploadError) {
            console.error('❌ Frontend: Failed to upload resume:', uploadError);
            throw new Error(`Failed to upload resume: ${uploadError.message}`);
          }
        }

        // Upload profile image if new file selected
        if (profileImageFile) {
          try {
            profileImageUrl = await uploadFileToS3(profileImageFile, companyId, candidateId, 'profileImage');
          } catch (uploadError) {
            console.error('❌ Frontend: Failed to upload profile image:', uploadError);
            throw new Error(`Failed to upload profile image: ${uploadError.message}`);
          }
        }

        setUploadingFiles(false);
      }

      // Prepare candidate data with S3 URLs
      const candidateData = {
        name: data.name,
        email: data.email,
        mobileNumber: data.mobileNumber || '',
        address: data.address || '',
        folderId: data.folderId || '',
        resume: resumeUrl,
        profileImage: profileImageUrl
      };


      const response = await candidatesAPI2.update(candidateId, candidateData);
      
      
      toast({
        title: "Success",
        description: "Candidate updated successfully",
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('❌ Frontend: Error updating candidate:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update candidate";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploadingFiles(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Candidate</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobileNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter mobile number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter address" 
                        className="resize-none"
                        rows={3}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
              control={form.control}
              name="folderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Folder (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a folder or leave empty for Others" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loadingFolders ? (
                        <SelectItem value="loading" disabled>
                          Loading folders...
                        </SelectItem>
                      ) : (
                        folders.map((folder) => (
                          <SelectItem key={folder._id} value={folder._id}>
                            <div className="flex items-center">
                              <div 
                                className="mr-2 h-3 w-3 rounded-full"
                                style={{ backgroundColor: folder.color }}
                              />
                              {folder.name}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Resume Upload */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Resume
              </FormLabel>
              {existingResume && !resumeFile && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-sm flex-1">Current resume uploaded</span>
                  <a 
                    href={existingResume} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              )}
              <FormDescription className="text-xs">
                Upload a new resume to replace the existing one (PDF only)
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={resumeInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleResumeChange}
                  className="flex-1"
                />
                {resumeFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeResume}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {resumeFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  New: {resumeFile.name}
                </p>
              )}
            </div>

            {/* Profile Picture Upload */}
            <div className="space-y-2">
              <FormLabel className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Picture (Optional)
              </FormLabel>
              {profileImagePreview && (
                <div className="mt-2">
                  <img
                    src={profileImagePreview}
                    alt="Profile preview"
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                </div>
              )}
              <FormDescription className="text-xs">
                Upload a new profile picture (JPG, PNG, etc.)
              </FormDescription>
              <div className="flex items-center gap-2">
                <Input
                  ref={profileImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="flex-1"
                />
                {profileImageFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeProfileImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {profileImageFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <User className="h-4 w-4" />
                  New image selected
                </p>
              )}
            </div>

              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={onClose} type="button" disabled={loading || uploadingFiles}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || uploadingFiles}>
                  {(loading || uploadingFiles) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {uploadingFiles ? 'Uploading Files...' : loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditCandidateModal;